"use client";

import { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AddPurchaseBill = () => {
  const navigate = useNavigate();

  const [merchants, setMerchants] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form, setForm] = useState({
    invoice_id: "",
    date: new Date().toISOString().split("T")[0],
    invoice_month: "",
    invoice_year: "",
    merchant_name: "",
    merchant_email: "",
    merchant_phone: "",
    merchant_address: "",
    merchant_state: "",
    place_of_supply: "",
    gst_amount: "",
    net_amount: "",
    total_amount: "",
    no_of_items: "",
    invoice_link: "",
  });

  useEffect(() => {
    fetchMerchants();
  }, []);

  const fetchMerchants = async () => {
    const res = await axios.get(
      "https://logicx.premierlam.com/api/get-pixel-purchase-bills-merchant",
      { headers: { authorization: localStorage.getItem("accessToken") } }
    );
    setMerchants(res.data.data || []);
  };

  const merchantOptions = merchants.map((m) => ({
    value: m.merchant_name,
    label: m.merchant_name,
    data: m,
  }));

  const handleMerchantSelect = (selected) => {
    const m = selected.data;
    setForm((prev) => ({
      ...prev,
      merchant_name: m.merchant_name,
      merchant_email: m.merchant_emails,
      merchant_phone: m.merchant_phone,
      merchant_address: m.merchant_address,
      merchant_state: m.merchant_state,
    }));
  };

  const formatDateForAPI = (isoDate) => {
    const d = new Date(isoDate);
    const day = String(d.getDate()).padStart(2, "0");
    const month = d.toLocaleString("en", { month: "short" }).toLowerCase();
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const selectedDate = new Date(form.date);

    const payload = {
      ...form,
      date: formatDateForAPI(form.date),
      invoice_month: selectedDate.toLocaleString("en", { month: "long" }),
      invoice_year: selectedDate.getFullYear().toString(),
    };

    try {
      setIsSubmitting(true);
      await axios.post(
        "https://logicx.premierlam.com/api/add-pixel-purchase-bills",
        payload,
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
            "Content-Type": "application/json",
          },
        }
      );

      toast.success("Purchase bill added successfully!");
      setTimeout(() => navigate("/all-purchase-bill"), 800);
    } catch {
      toast.error("Failed to add purchase bill");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto bg-white p-6 rounded-lg border">
          <h1 className="text-2xl font-semibold mb-6">Add Purchase Bill</h1>

          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">

            {/* Invoice ID */}
            <div>
              <label className="text-sm font-medium">Invoice ID</label>
              <input
                required
                className="border p-2 rounded w-full"
                onChange={(e) =>
                  setForm({ ...form, invoice_id: e.target.value })
                }
              />
            </div>

            {/* Date */}
            <div>
              <label className="text-sm font-medium">Invoice Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) =>
                  setForm({ ...form, date: e.target.value })
                }
                className="border p-2 rounded w-full"
                required
              />
            </div>

            {/* Merchant */}
            <div className="col-span-1">
              <label className="text-sm font-medium">Merchant</label>
              <Select
                options={merchantOptions}
                onChange={handleMerchantSelect}
                placeholder="Select Merchant"
              />
            </div>

            {/* Auto filled */}
            <div>
              <label className="text-sm font-medium">Merchant Email</label>
              <input
                disabled
                value={form.merchant_email}
                className="border p-2 bg-gray-100 rounded w-full"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Merchant Phone</label>
              <input
                disabled
                value={form.merchant_phone}
                className="border p-2 bg-gray-100 rounded w-full"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Merchant Address</label>
              <input
                disabled
                value={form.merchant_address}
                className="border p-2 bg-gray-100 rounded w-full"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Merchant State</label>
              <input
                disabled
                value={form.merchant_state}
                className="border p-2 bg-gray-100 rounded w-full"
              />
            </div>

            {/* Other inputs */}
            {[
              ["place_of_supply", "Place of Supply"],
              ["gst_amount", "GST Amount"],
              ["net_amount", "Net Amount"],
              ["total_amount", "Total Amount"],
              ["no_of_items", "No of Items"],
            ].map(([key, label]) => (
              <div key={key}>
                <label className="text-sm font-medium">{label}</label>
                <input
                  required
                  className="border p-2 rounded w-full"
                  onChange={(e) =>
                    setForm({ ...form, [key]: e.target.value })
                  }
                />
              </div>
            ))}

            {/* Invoice Link */}
            <div className="col-span-2">
              <label className="text-sm font-medium">Invoice Link</label>
              <input
                required
                className="border p-2 rounded w-full"
                onChange={(e) =>
                  setForm({ ...form, invoice_link: e.target.value })
                }
              />
            </div>

            {/* Buttons */}
            <div className="col-span-2 flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={() => navigate("/purchase-bills")}
                className="px-4 py-2 bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                disabled={isSubmitting}
                className="px-4 py-2 bg-orange-500 text-white rounded"
              >
                {isSubmitting ? "Adding..." : "Add Purchase Bill"}
              </button>
            </div>

          </form>
        </div>
      </div>
    </>
  );
};

export default AddPurchaseBill;
